import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseCICDPipelineSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage CI/CD pipeline versions
 * Stores and tracks different versions of CI/CD pipeline from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useCICDPipelineVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for CI/CD pipeline version management
  const cicdPipelineConfig = {
    // Determine if a message contains CI/CD pipeline content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      // More specific matching to avoid false positives
      const hasCICDPipelineKeywords =
             content.includes('CI/CD Pipeline') ||
             content.includes('CI/CD') ||
             content.includes('Continuous Integration') ||
             content.includes('Continuous Deployment') ||
             content.includes('Continuous Delivery') ||
             content.includes('Build Stage') ||
             content.includes('Test Stage') ||
             content.includes('Deploy Stage') ||
             content.includes('Pipeline Configuration') ||
             content.includes('GitHub Actions') ||
             content.includes('GitLab CI') ||
             content.includes('Jenkins') ||
             content.includes('Azure DevOps') ||
             content.includes('CircleCI') ||
             content.includes('pipeline:') ||
             content.includes('stages:') ||
             content.includes('jobs:');
      // Must have CI/CD pipeline keywords, not just any markdown headers
      return hasCICDPipelineKeywords;
    },

    // Parse CI/CD pipeline sections from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseCICDPipelineSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedSections, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        sections: parsedSections,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(cicdPipelineConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      sections: versionData.currentVersion.parsedContent || versionData.currentVersion.sections
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to CI/CD pipeline-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useCICDPipelineVersions;
